/**
 * TrendsAvailabilityCard
 *
 * Testing tool: for one entity (provider/organization + NPI or LN claims id),
 * probes the historical data tree on data.doctivityhealth.com for every
 * quarter in the configured trending range and every data type, and shows a
 * green check (file present with rows) or red X (no file / empty) per cell.
 *
 * Uses the same trending store as the Trends tab, so results are cached and
 * revisits are answered by the browser HTTP cache.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { ColorUtil } from 'utils';
import { DynamicTable } from 'components';

// Material Design Icons glyphs (mdi-check-bold / mdi-close-thick), inlined so
// no icon package needs adding — the MUI set has no bold/thick variants.
const MDI_CHECK_BOLD =
  'M9,20.42L2.79,14.21L5.62,11.38L9,14.77L18.88,4.88L21.71,7.71L9,20.42Z';
const MDI_CLOSE_THICK =
  'M20 6.91L17.09 4L12 9.09L6.91 4L4 6.91L9.09 12L4 17.09L6.91 20L12 14.91L17.09 20L20 17.09L14.91 12L20 6.91Z';

function MdiIcon({ path, color }) {
  return (
    <svg
      viewBox='0 0 24 24'
      width={18}
      height={18}
      style={{ verticalAlign: 'middle' }}
    >
      <path d={path} fill={color} />
    </svg>
  );
}
MdiIcon.propTypes = {
  path: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
};
import {
  loadTrendingData,
  trendingKey,
  TrendingDataType,
  TrendingEntityType,
} from 'store/actions/trendingActions';
import CLAIMS_CFG from '../../config/claims-date.js';

const styles = (theme) => ({
  card: {
    marginBottom: theme.spacing(3),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
  },
  title: {
    flex: 1,
    minWidth: 240,
  },
  npiField: {
    width: 180,
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    borderCollapse: 'collapse',
    fontSize: 12,
    whiteSpace: 'nowrap',
  },
  th: {
    padding: '6px 10px',
    color: '#64748B',
    fontWeight: 600,
    textTransform: 'uppercase',
    fontSize: 10,
    textAlign: 'center',
    backgroundColor: '#EBF1F8',
  },
  typeCell: {
    padding: '6px 10px',
    fontWeight: 500,
    textAlign: 'left',
    backgroundColor: '#F8FAFC',
  },
  cell: {
    padding: '4px 10px',
    textAlign: 'center',
    borderBottom: '1px solid #F1F5F9',
  },
  ok: {
    color: '#2E7D32',
    fontSize: 18,
    verticalAlign: 'middle',
  },
  missing: {
    color: '#C62828',
    fontSize: 18,
    verticalAlign: 'middle',
  },
  errorIcon: {
    color: '#ED6C02',
    fontSize: 18,
    verticalAlign: 'middle',
  },
});

const DATA_TYPES = [
  { type: TrendingDataType.PROJECTED_PATIENTS, label: 'Projected Patients' },
  {
    type: TrendingDataType.PEDIATRIC_PROJECTED_PATIENTS,
    label: 'Pediatric Projected Patients',
  },
  { type: TrendingDataType.CAPTURED_CLAIMS, label: 'Captured Claims' },
  // LN keys migration rows by FACILITY — the files exist only in the
  // organizations tree, so for providers this row is n/a, not missing
  { type: TrendingDataType.PATIENT_MIGRATION, label: 'Patient Migration', orgOnly: true },
];

function trendingQuarters() {
  const { trendingStartQuarter: start, trendingEndQuarter: end } = CLAIMS_CFG;
  const quarters = [];
  let { year, quarter } = start;
  while (year < end.year || (year === end.year && quarter <= end.quarter)) {
    quarters.push({ year, quarter });
    quarter++;
    if (quarter > 4) {
      quarter = 1;
      year++;
    }
  }
  return quarters;
}

function TrendsAvailabilityCard({ classes }) {
  const dispatch = useDispatch();
  const quarters = useMemo(() => trendingQuarters(), []);

  // known-good defaults per entity type; switching the dropdown swaps one
  // default for the other so the probe targets an entity that can exist
  const DEFAULT_IDS = {
    [TrendingEntityType.PROVIDER]: '1518329739',
    [TrendingEntityType.ORGANIZATION]: '1366479099',
  };

  // form state (what's typed) vs applied state (what the table shows)
  const [formEntityType, setFormEntityType] = useState(TrendingEntityType.ORGANIZATION);
  const [formId, setFormId] = useState(DEFAULT_IDS[TrendingEntityType.ORGANIZATION]);
  const [applied, setApplied] = useState({
    entityType: TrendingEntityType.ORGANIZATION,
    id: DEFAULT_IDS[TrendingEntityType.ORGANIZATION],
  });

  const onEntityTypeChange = (newType) => {
    setFormEntityType(newType);
    // only swap ids the user hasn't customized: if the field still holds the
    // OTHER type's default, replace it with the new type's default
    const otherType =
      newType === TrendingEntityType.PROVIDER
        ? TrendingEntityType.ORGANIZATION
        : TrendingEntityType.PROVIDER;
    if (formId.trim() === DEFAULT_IDS[otherType]) {
      setFormId(DEFAULT_IDS[newType]);
    }
  };

  const keys = useMemo(() => {
    const out = [];
    DATA_TYPES.forEach((dt) => {
      quarters.forEach((q) => {
        out.push(
          trendingKey(applied.entityType, applied.id, q.year, q.quarter, dt.type),
        );
      });
    });
    return out;
  }, [applied, quarters]);

  const entries = useSelector(
    (state) => keys.map((k) => state.trending.entries[k]),
    shallowEqual,
  );
  const entryByKey = useMemo(() => {
    const map = {};
    keys.forEach((k, i) => {
      map[k] = entries[i];
    });
    return map;
  }, [keys, entries]);

  const isNa = (dt) =>
    dt.orgOnly && applied.entityType !== TrendingEntityType.ORGANIZATION;

  useEffect(() => {
    if (!applied.id) return;
    DATA_TYPES.forEach((dt) => {
      if (isNa(dt)) return; // don't probe combinations that can't exist
      quarters.forEach((q) => {
        dispatch(
          loadTrendingData(applied.entityType, applied.id, q.year, q.quarter, dt.type),
        );
      });
    });
  }, [dispatch, applied, quarters]);

  const onCheck = () => {
    setApplied({ entityType: formEntityType, id: formId.trim() });
  };

  const renderCell = (entry) => {
    if (!entry || entry.loading) {
      return <CircularProgress size={12} thickness={5} />;
    }
    if (entry.error) {
      return (
        <Tooltip title={String(entry.error)}>
          <WarningAmberIcon className={classes.errorIcon} />
        </Tooltip>
      );
    }
    if (entry.loaded && entry.data && entry.data.length > 0) {
      return (
        <Tooltip title={`${entry.data.length.toLocaleString()} rows`}>
          <span>
            <MdiIcon path={MDI_CHECK_BOLD} color={ColorUtil.GreenColor} />
          </span>
        </Tooltip>
      );
    }
    return <MdiIcon path={MDI_CLOSE_THICK} color={ColorUtil.RedColor} />;
  };

  // DynamicTable wiring: one row per data type, one column per quarter.
  // Rows carry only identity; the per-quarter formatters read the live store
  // entry, so cells update as probes land.
  const tableColumns = useMemo(
    () => [
      {
        label: 'Data Type',
        key: 'label',
        sortable: false,
        style: { whiteSpace: 'nowrap' },
      },
      ...quarters.map((q) => ({
        label: `${q.year} Q${q.quarter}`,
        key: `q_${q.year}_${q.quarter}`,
        sortable: false,
        style: { textAlign: 'center', width: 76 },
        format: (value, row) => {
          const dt = DATA_TYPES.find((d) => d.type === row.dataType);
          if (dt && isNa(dt)) {
            return (
              <Tooltip title='Organizations only'>
                <span>—</span>
              </Tooltip>
            );
          }
          const key = trendingKey(
            applied.entityType,
            applied.id,
            q.year,
            q.quarter,
            row.dataType,
          );
          return renderCell(entryByKey[key]);
        },
      })),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [quarters, applied, entryByKey],
  );

  const tableRows = useMemo(
    () => DATA_TYPES.map((dt) => ({ dataType: dt.type, label: dt.label })),
    [],
  );

  return (
    <Card className={classes.card}>
      <CardContent>
        <div className={classes.header}>
          <Typography
            component='h2'
            variant='h6'
            color='primary'
            className={classes.title}
          >
            Trends Data Availability
          </Typography>
          <Select
            size='small'
            value={formEntityType}
            onChange={(e) => onEntityTypeChange(e.target.value)}
          >
            <MenuItem value={TrendingEntityType.PROVIDER}>Provider</MenuItem>
            <MenuItem value={TrendingEntityType.ORGANIZATION}>
              Organization
            </MenuItem>
          </Select>
          <TextField
            size='small'
            className={classes.npiField}
            label='NPI'
            value={formId}
            onChange={(e) => setFormId(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onCheck();
            }}
          />
          <Button variant='contained' color='primary' onClick={onCheck}>
            Check
          </Button>
        </div>
        <div className={classes.tableWrap}>
          <DynamicTable
            columns={tableColumns}
            data={tableRows}
            idKey='dataType'
            showFilters={false}
            noDataMessage='No data types configured.'
          />
        </div>
      </CardContent>
    </Card>
  );
}

TrendsAvailabilityCard.propTypes = {
  classes: PropTypes.object.isRequired,
};

const Styled = withStyles(styles)(TrendsAvailabilityCard);
export { Styled as TrendsAvailabilityCard };
